
// 新闻采集服务
export interface NewsItem {
  id: number;
  title: string;
  summary: string;
  publishTime: string;
  views: string;
  category: string;
  url?: string;
}

// 由于浏览器CORS限制，我们使用模拟的采集功能
// 实际部署时可以通过后端服务器进行采集
export class NewsService {
  private static instance: NewsService;
  private newsCache: NewsItem[] = [];
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

  private constructor() {}

  public static getInstance(): NewsService {
    if (!NewsService.instance) {
      NewsService.instance = new NewsService();
    }
    return NewsService.instance;
  }

  // 模拟从网易体育采集新闻
  public async fetchSportsNews(): Promise<NewsItem[]> {
    const now = Date.now();
    
    // 如果缓存有效，直接返回缓存数据
    if (this.newsCache.length > 0 && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return this.newsCache;
    }

    try {
      // 模拟网络请求延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 模拟采集到的新闻数据（实际应该从API获取）
      const simulatedNews: NewsItem[] = [
        {
          id: Date.now() + 1,
          title: '【实时采集】NBA季后赛激战正酣，湖人队主场取得关键胜利',
          summary: '在刚刚结束的NBA季后赛第五场比赛中，洛杉矶湖人队在主场以112-108险胜对手，将总比分扳成3-2...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 120) + 10),
          views: this.generateViews(),
          category: '篮球',
          url: 'https://sports.163.com'
        },
        {
          id: Date.now() + 2,
          title: '【最新消息】欧洲杯预选赛：意大利队客场2-1战胜英格兰',
          summary: '在今晚进行的欧洲杯预选赛中，意大利队客场挑战英格兰队，最终以2-1的比分取得胜利...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 180) + 20),
          views: this.generateViews(),
          category: '足球',
          url: 'https://sports.163.com'
        },
        {
          id: Date.now() + 3,
          title: '【突发】网球大师赛决赛：德约科维奇直落三盘夺冠',
          summary: '在网球大师赛决赛中，塞尔维亚天王德约科维奇表现出色，直落三盘战胜对手夺得冠军...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 240) + 30),
          views: this.generateViews(),
          category: '网球',
          url: 'https://sports.163.com'
        },
        {
          id: Date.now() + 4,
          title: '【重磅】中国女排世界联赛首战告捷，3-0横扫对手',
          summary: '在世界女排联赛首站比赛中，中国女排发挥出色，以3-0的比分横扫对手，取得开门红...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 300) + 45),
          views: this.generateViews(),
          category: '排球',
          url: 'https://sports.163.com'
        },
        {
          id: Date.now() + 5,
          title: '【精彩】世界乒乓球锦标赛：马龙晋级男单四强',
          summary: '在世乒赛男单1/4决赛中，中国选手马龙以4-1战胜对手，成功晋级四强，向卫冕冠军发起冲击...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 360) + 60),
          views: this.generateViews(),
          category: '乒乓球',
          url: 'https://sports.163.com'
        },
        {
          id: Date.now() + 6,
          title: '【热点】全英羽毛球公开赛：石宇奇逆转晋级决赛',
          summary: '在全英羽毛球公开赛男单半决赛中，中国选手石宇奇在先失一局的情况下连扳两局，成功晋级决赛...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 420) + 75),
          views: this.generateViews(),
          category: '羽毛球',
          url: 'https://sports.163.com'
        },
        {
          id: Date.now() + 7,
          title: '【转会】皇马官宣签下新援，转会费创俱乐部纪录',
          summary: '皇家马德里足球俱乐部今日官方宣布，以创纪录的转会费签下了法国国脚中场球员...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 480) + 90),
          views: this.generateViews(),
          category: '足球',
          url: 'https://sports.163.com'
        },
        {
          id: Date.now() + 8,
          title: '【分析】CBA总决赛前瞻：广东宏远VS辽宁本钢',
          summary: 'CBA总决赛即将开打，卫冕冠军广东宏远将主场迎战辽宁本钢，双方实力接近，比赛悬念十足...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 540) + 105),
          views: this.generateViews(),
          category: '篮球',
          url: 'https://sports.163.com'
        }
      ];

      this.newsCache = simulatedNews;
      this.lastFetchTime = now;
      
      console.log('🔄 新闻采集完成，获取到', simulatedNews.length, '条最新体育新闻');
      return simulatedNews;
      
    } catch (error) {
      console.error('新闻采集失败:', error);
      // 返回缓存数据或默认数据
      return this.newsCache.length > 0 ? this.newsCache : this.getDefaultNews();
    }
  }

  // 新增：采集指定数量的最新新闻
  public async fetchLatestNews(count: number = 10): Promise<NewsItem[]> {
    try {
      // 模拟网络请求延迟
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const now = Date.now();
      
      // 生成更多样化的新闻标题
      const newsTemplates = [
        {
          title: '【重磅】国际奥委会宣布新项目加入奥运会',
          summary: '国际奥委会在最新会议中决定，将新增多个竞技项目加入下届奥运会比赛项目...',
          category: '综合'
        },
        {
          title: '【突发】世界杯预选赛爆冷：弱队3-0击败强队',
          summary: '在今晚进行的世界杯预选赛中，排名较低的球队以3-0的悬殊比分击败了夺冠热门...',
          category: '足球'
        },
        {
          title: '【最新】NBA总决赛MVP候选人出炉',
          summary: 'NBA总决赛激战正酣，多位球星表现出色，MVP候选人名单正式公布...',
          category: '篮球'
        },
        {
          title: '【热点】温网女单决赛：新星击败卫冕冠军',
          summary: '在温布尔登网球锦标赛女单决赛中，年仅19岁的新星选手击败了卫冕冠军...',
          category: '网球'
        },
        {
          title: '【独家】中国游泳队备战世锦赛，多项纪录有望刷新',
          summary: '中国游泳队正在紧张备战即将到来的世界游泳锦标赛，多名选手状态出色...',
          category: '游泳'
        },
        {
          title: '【分析】F1赛车技术革新，电动化趋势明显',
          summary: 'F1赛事正在经历技术革命，电动化和环保技术成为各车队关注焦点...',
          category: '赛车'
        },
        {
          title: '【消息】东京马拉松创参赛人数纪录',
          summary: '东京马拉松赛事吸引了创纪录的参赛人数，来自世界各地的跑者齐聚一堂...',
          category: '田径'
        },
        {
          title: '【前瞻】欧洲杯八强对阵出炉，强强对话即将上演',
          summary: '欧洲杯小组赛全部结束，八强对阵表正式确定，多场强强对话值得期待...',
          category: '足球'
        },
        {
          title: '【重要】国际体育仲裁法庭最新裁决出炉',
          summary: '国际体育仲裁法庭对近期多起争议案件做出最终裁决，影响深远...',
          category: '综合'
        },
        {
          title: '【爆料】知名运动员宣布退役，结束辉煌职业生涯',
          summary: '一位在国际赛场上屡创佳绩的知名运动员今日正式宣布退役...',
          category: '综合'
        }
      ];
      
      // 随机选择新闻模板并生成唯一ID
      const selectedNews: NewsItem[] = [];
      for (let i = 0; i < Math.min(count, newsTemplates.length); i++) {
        const template = newsTemplates[Math.floor(Math.random() * newsTemplates.length)];
        const newsItem: NewsItem = {
          id: now + i + Math.floor(Math.random() * 1000),
          title: template.title,
          summary: template.summary,
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 60) + 1), // 1-60分钟前
          views: this.generateViews(),
          category: template.category,
          url: 'https://sports.163.com'
        };
        
        // 避免重复选择同一模板
        const existingTitles = selectedNews.map(news => news.title);
        if (!existingTitles.includes(newsItem.title)) {
          selectedNews.push(newsItem);
        }
      }
      
      // 如果选中的新闻不足count数量，用变体填充
      while (selectedNews.length < count) {
        const baseTemplate = newsTemplates[Math.floor(Math.random() * newsTemplates.length)];
        const variant: NewsItem = {
          id: now + selectedNews.length + Math.floor(Math.random() * 10000),
          title: `【最新】${baseTemplate.title.replace('【重磅】', '').replace('【突发】', '').replace('【热点】', '').replace('【最新】', '').replace('【独家】', '').replace('【分析】', '').replace('【消息】', '').replace('【前瞻】', '').replace('【重要】', '').replace('【爆料】', '')}`,
          summary: baseTemplate.summary + '最新消息显示，相关各方正在密切关注事态发展...',
          publishTime: this.getTimeAgo(Math.floor(Math.random() * 30) + 1),
          views: this.generateViews(),
          category: baseTemplate.category,
          url: 'https://sports.163.com'
        };
        selectedNews.push(variant);
      }
      
      console.log('🔄 采集最新新闻完成，获取到', selectedNews.length, '条新闻');
      return selectedNews.slice(0, count);
      
    } catch (error) {
      console.error('最新新闻采集失败:', error);
      return [];
    }
  }

  // 生成随机浏览量
  private generateViews(): string {
    const views = Math.floor(Math.random() * 50000) + 1000;
    if (views >= 10000) {
      return (views / 10000).toFixed(1) + '万';
    }
    return views.toString();
  }

  // 生成时间描述 - 改为采集时间格式
  private getTimeAgo(minutes: number): string {
    const now = new Date();
    const targetTime = new Date(now.getTime() - minutes * 60000);
    
    // 返回具体的采集时间
    const hours = targetTime.getHours().toString().padStart(2, '0');
    const mins = targetTime.getMinutes().toString().padStart(2, '0');
    
    if (minutes < 60) {
      return `${hours}:${mins}`;
    } else if (minutes < 1440) {
      const month = (targetTime.getMonth() + 1).toString().padStart(2, '0');
      const day = targetTime.getDate().toString().padStart(2, '0');
      return `${month}-${day} ${hours}:${mins}`;
    } else {
      const year = targetTime.getFullYear();
      const month = (targetTime.getMonth() + 1).toString().padStart(2, '0');
      const day = targetTime.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${mins}`;
    }
  }

  // 获取默认新闻数据
  private getDefaultNews(): NewsItem[] {
    return [
      {
        id: 999001,
        title: '【默认】暂时无法获取最新体育新闻',
        summary: '网络连接或服务暂时不可用，请稍后刷新页面重试',
        publishTime: '刚刚',
        views: '0',
        category: '公告',
        url: ''
      }
    ];
  }

  // 清除缓存
  public clearCache(): void {
    this.newsCache = [];
    this.lastFetchTime = 0;
  }

  // 获取缓存状态
  public getCacheStatus(): { hasCache: boolean; lastFetch: string; itemCount: number } {
    return {
      hasCache: this.newsCache.length > 0,
      lastFetch: this.lastFetchTime > 0 ? new Date(this.lastFetchTime).toLocaleTimeString() : '未采集',
      itemCount: this.newsCache.length
    };
  }
}

// 导出单例实例
export const newsService = NewsService.getInstance();
